import { apiUrl } from "@/lib/api";
import { goAnchor } from "./app-nav";
import { importErrorText, importsApi, showImportContacts, type ImportRun } from "./contacts-api";
import { cn } from "./ui";
import { locale, t } from "@/lib/i18n";

const STATUS: Record<ImportRun["status"], string> = { queued: t("В очереди"), running: t("Загружается"), done: t("Завершено"), failed: t("Не выполнен") };

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" }) : "—");

type Props = { run: ImportRun; onBack: () => void };

/** Результат одного импорта: итоги, пропущенные строки с причиной и переход к загруженным контактам. */
export default function ImportResult({ run, onBack }: Props) {
  const openContacts = () => {
    showImportContacts(run);
    goAnchor("#contacts-app");
  };

  const cards = [
    { label: t("Строк в файле"), value: run.rows, icon: "ri-file-list-3-line", tone: "text-muted-foreground" },
    { label: t("Создано"), value: run.created, icon: "ri-user-add-line", tone: "text-primary" },
    { label: t("Обновлено"), value: run.updated, icon: "ri-refresh-line", tone: "text-sky-600" },
    { label: t("Пропущено"), value: run.skipped, icon: "ri-error-warning-line", tone: run.skipped ? "text-amber-600" : "text-muted-foreground" },
  ];

  return (
    <div className="p-3.5 md:p-5">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex cursor-pointer items-center gap-1 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground"
      >
        <i className="ri-arrow-left-line leading-none" aria-hidden />
        {t("Все импорты")}
      </button>

      <div className="mt-3 flex flex-wrap items-start gap-2.5">
        <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
          <i className="ri-file-text-line text-base leading-none" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold leading-snug">{run.fileName}</h3>
          <p className="mt-1 text-[12px] text-muted-foreground">
            {STATUS[run.status]} · {t("начат {when}", { when: when(run.createdAt) })}
            {run.finishedAt ? ` · ${t("закончен {when}", { when: when(run.finishedAt) })}` : ""}
            {run.author ? ` · ${run.author}` : ""}
          </p>
        </div>
        {run.status === "done" && run.created + run.updated > 0 ? (
          <button
            type="button"
            onClick={openContacts}
            className="inline-flex h-8 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
          >
            <i className="ri-contacts-line" aria-hidden />
            {t("Открыть контакты импорта")}
          </button>
        ) : null}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-btn border border-border bg-background/60 px-3 py-2.5">
            <p className={cn("flex items-center gap-1.5 text-[11px] font-semibold", c.tone)}>
              <i className={cn(c.icon, "text-sm leading-none")} aria-hidden />
              {c.label}
            </p>
            <p className="mt-1 text-xl font-semibold tabular-nums">{c.value.toLocaleString(locale)}</p>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[11.5px] leading-relaxed text-muted-foreground">
        {t("«Обновлено» — контакты, которые уже были в базе или повторялись в файле (совпали по ID, почте или телефону): их дополнили данными из строки, дубли не создавались.")}
      </p>

      {run.status === "failed" ? (
        <p className="mt-4 rounded-btn border border-red-500/30 bg-red-500/5 px-3 py-2.5 text-[12px] text-red-600">
          {t("Импорт не выполнен: {error}. Попробуйте загрузить файл ещё раз или напишите в «Идея / проблема».", { error: run.errors[0]?.error ?? t("неизвестная ошибка") })}
        </p>
      ) : null}

      {run.status === "done" && run.errors.length > 0 ? (
        <div className="mt-5">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="flex-1 text-[13px] font-semibold">{t("Пропущенные строки")}</h4>
            <a
              href={apiUrl(importsApi.skippedPath(run.id))}
              download
              className="inline-flex h-8 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-border bg-background px-3 text-[12px] font-semibold text-foreground transition hover:bg-muted"
            >
              <i className="ri-file-download-line" aria-hidden />
              {t("Скачать пропущенные строки")}
            </a>
          </div>
          <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
            {t("Как исправить: скачайте файл — в нём только эти строки и колонка «Ошибка» с причиной. Поправьте значения в Excel, сохраните как CSV и загрузите через «Импорт контактов»: исправленные строки добавятся, уже загруженные контакты не задвоятся. Одну-две строки проще добавить вручную в разделе «Контакты» → «Добавить контакт».")}
          </p>
          <div className="mt-3 overflow-x-auto rounded-btn border border-border">
            <table className="w-full min-w-[480px] border-collapse text-start">
              <thead>
                <tr className="border-b border-border bg-background/60">
                  {[t("Строка файла"), t("Причина"), t("Значение")].map((h) => (
                    <th key={h} scope="col" className="px-3 py-2 text-start text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {run.errors.map((e, i) => (
                  <tr key={`${e.line}-${i}`} className="border-b border-border/60 last:border-0">
                    <td className="px-3 py-2 text-[12px] tabular-nums">{e.line}</td>
                    <td className="px-3 py-2 text-[12px]">{importErrorText[e.error] ?? e.error}</td>
                    <td className="max-w-[16rem] truncate px-3 py-2 text-[12px] text-muted-foreground">{e.value || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {run.skipped > run.errors.length ? (
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              {t("Показаны первые {shown} из {total} — остальные с той же причиной, исправьте показанные и загрузите файл снова.", { shown: run.errors.length, total: run.skipped })}
            </p>
          ) : null}
        </div>
      ) : null}

      {run.status === "done" && run.errors.length === 0 ? (
        <p className="mt-5 flex items-center gap-2 text-[12.5px] text-primary">
          <i className="ri-checkbox-circle-line text-base leading-none" aria-hidden />
          {t("Все строки загружены без ошибок.")}
        </p>
      ) : null}
    </div>
  );
}
